"use client";

import { ReactNode, isValidElement, Children } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { InquiryForm } from "@/components/forms/InquiryForm";

type Props = {
  title: string;
  slug: string;
  /** The trigger element (a button/link). Receives no props; style it yourself. */
  trigger: ReactNode;
  /** Dialog heading — defaults to `title`. */
  dialogTitle?: string;
  /** Overrides the form's auto-derived "Inquiry: {title}" subject line. */
  defaultSubject?: string;
  /** Pre-fills the message textarea, e.g. with a specific departure date. */
  defaultMessage?: string;
};

/**
 * Inquiry form in a modal dialog. Keeps the long form out of the sticky booking
 * card and the mobile bar — both just render a trigger and share this island.
 */
export function InquiryDialog({
  title,
  slug,
  trigger,
  dialogTitle,
  defaultSubject,
  defaultMessage,
}: Props) {
  // Radix `asChild` requires exactly one valid React element. When the caller
  // passes a single element (the usual case: a <button>), slot onto it so we
  // don't render a wrapping <button> around it (which would nest <button>s and
  // produce invalid HTML). Only when the trigger is NON-element content
  // (string / null / array) do we render Radix's own <button> with that content.
  const asChild = Children.count(trigger) === 1 && isValidElement(trigger);
  return (
    <Dialog.Root>
      {asChild ? (
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      ) : (
        <Dialog.Trigger className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-primary px-5 py-2 text-xs font-bold tracking-wide text-primary transition-colors hover:bg-primary hover:text-white">
          {trigger ?? "Make an Enquiry"}
        </Dialog.Trigger>
      )}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:inset-x-auto sm:left-1/2 sm:top-1/2 sm:bottom-auto sm:w-[min(92vw,32rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl">
          <div className="flex items-start justify-between">
            <Dialog.Title className="font-display text-xl font-bold text-headings">
              {dialogTitle ?? title}
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-soft hover:bg-bg"
            >
              <X size={18} aria-hidden />
            </Dialog.Close>
          </div>
          <div className="mt-5">
            <InquiryForm
              tripTitle={title}
              tripSlug={slug}
              defaultSubject={defaultSubject}
              defaultMessage={defaultMessage}
            />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
